import { useState } from "react";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  ColumnDef,
  Table as TanstackTable,
} from "@tanstack/react-table";
import { Download, Search } from "lucide-react";
import {
  Button,
  Input,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table,
} from "@pushpress/pushpress-ui";
import VerticalDivider from "../shared/VerticalDivider";
import TableTabs from "./TableTabs";
import TableSkeleton from "./TableSkeleton";
import EmptyTable from "../shared/EmptyTable";
import TablePagination from "./TablePagination";
import { DropdownFilter } from "./DropdownFilter";
import { MultiDropdownFilter } from "./MultiDropdownFilter";
import ErrorState from "../shared/ErrorState";
import ExportPeopleModal from "./ExportPeopleModal";
import { getDefaultFilters } from "../../helpers";
import { useExperiment } from "../../../../hooks/useExperiment";
import { SHOW_MEMBER_PROFILE_FEATURE_FLAG } from "../../../../context/features/features.utils";
import {
  leadStatusFilterOptions,
  memberChurnDateFilterOptions,
  memberSubscriptionStatusFilterOptions,
  memberTypesFilterOptions,
  PAGINATION_PAGE_SIZE,
  staffRolesFilterOptions,
} from "../../constants";
import {
  LeadRecordType,
  MemberRecordType,
  PEOPLE_TYPE,
  PeopleType,
  StaffRecordType,
} from "../../types";

export type PersonRecord = MemberRecordType | LeadRecordType | StaffRecordType;

type PeopleTableProps = {
  activeTab: PeopleType;
  columns: ColumnDef<any>[];
  data: PersonRecord[];
  handleTabChange: (tab: PeopleType) => void;
  onRowSelectionChange: (selected: PersonRecord[]) => void;
  isError: boolean;
  isLoading: boolean;
  refetch: () => void;
};

export default function PeopleTable({
  activeTab,
  columns,
  data,
  handleTabChange,
  isError,
  onRowSelectionChange,
  isLoading,
  refetch,
}: PeopleTableProps) {
  const { useExperimentValue } = useExperiment();
  const isMemberProfilePageEnabled = useExperimentValue(
    SHOW_MEMBER_PROFILE_FEATURE_FLAG,
    true,
  ) as boolean;

  const setDefaultMembersFilter = !!(
    activeTab === PEOPLE_TYPE.MEMBERS &&
    !isError &&
    data
  );

  const setDefaultLeadsFilter = !!(
    activeTab === PEOPLE_TYPE.LEADS &&
    !isError &&
    data
  );

  const showPagination = !isError && data.length > 0;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    getDefaultFilters(setDefaultMembersFilter, setDefaultLeadsFilter),
  );

  const [rowSelection, setRowSelection] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);

  // Type annotation needed because we conditionally supply different column sets
  // based on the active tab rather than using column visibility properties
  const table: TanstackTable<PersonRecord> = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: (updaterOrValue) => {
      setRowSelection(updaterOrValue);

      const newSelection =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      // selection is a map of row id to boolean
      const selectedRows = Object.keys(newSelection)
        .filter((key) => newSelection[key])
        .map((key) => data[parseInt(key)]);

      onRowSelectionChange(selectedRows);
    },
  });

  return (
    <>
      {showExportModal && (
        <ExportPeopleModal
          rowsSelected={table
            .getSelectedRowModel()
            .rows.map((row) => row.original.userUuid)}
          activeTab={activeTab}
          filters={
            // convert the columnFilters to an object with the column id as the key and the value as the filter value to match ExportPeopleFilterType
            columnFilters.reduce((acc, item) => {
              acc[item.id] = item.value;
              return acc;
            }, {})
          }
          closeModal={() => setShowExportModal(false)}
          total={table.getFilteredRowModel().rows.length}
        />
      )}
      <div className="tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-p-4 tw-gap-y-4">
        <div>
          <div className="tw-flex tw-items-center">
            <div className="max-sm:tw-flex-1">
              <TableTabs
                activeTab={activeTab}
                handleTabChange={handleTabChange}
              />
            </div>
            <div className="md:tw-block tw-hidden">
              <VerticalDivider />
            </div>
          </div>
        </div>
        <div className="tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-between tw-flex-nowrap">
          <div className="tw-relative tw-w-[50%] sm:tw-w-auto sm:tw-min-w-[260px]">
            <Search className="tw-absolute tw-left-2.5 tw-top-2.5 tw-h-4 tw-w-4 tw-text-neutral-500" />
            <Input
              placeholder={`Search ${activeTab}`}
              className="tw-w-full tw-pl-8 !tw-shadow-none"
              value={
                (table.getColumn("name")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("name")?.setFilterValue(event.target.value)
              }
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end">
            {activeTab === PEOPLE_TYPE.STAFF &&
              table.getColumn("membershipRole") && (
                <DropdownFilter
                  column={table.getColumn("membershipRole")}
                  options={staffRolesFilterOptions}
                  label="Staff role"
                />
              )}
            {activeTab === PEOPLE_TYPE.LEADS &&
              table.getColumn("leadStatus") && (
                <DropdownFilter
                  column={table.getColumn("leadStatus")}
                  options={leadStatusFilterOptions}
                  label="Lead status"
                />
              )}
            {activeTab === PEOPLE_TYPE.MEMBERS &&
              table.getColumn("membershipRole") &&
              table.getColumn("status") &&
              table.getColumn("churnDate") && (
                <MultiDropdownFilter
                  filters={[
                    {
                      column: table.getColumn("membershipRole"),
                      label: "Member type",
                      options: memberTypesFilterOptions,
                    },
                    {
                      column: table.getColumn("status"),
                      label: "Member status",
                      options: memberSubscriptionStatusFilterOptions,
                    },
                    {
                      column: table.getColumn("churnDate"),
                      label: "Churn date",
                      options: memberChurnDateFilterOptions,
                    },
                  ]}
                />
              )}
            <Button
              data-testid="download-button"
              variant="outline"
              className="!tw-h-8"
              onClick={() => setShowExportModal(true)}
            >
              <Download className="tw-h-4 tw-w-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="tw-ring-1 tw-ring-black/5">
        {isError ? (
          <ErrorState refetch={refetch} peopleType={activeTab} />
        ) : isLoading ? (
          <TableSkeleton />
        ) : (
          <Table className="tw-divide-y tw-divide-neutral-300">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="tw-bg-neutral-50">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="tw-divide-y tw-divide-neutral-200 tw-bg-white">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isClickable =
                        cell.column.id !== "select" &&
                        cell.column.id !== "actions";

                      return (
                        <TableCell
                          key={cell.id}
                          className={`tw-py-0 ${
                            cell.column.id === "select" ? "tw-w-[10px]" : ""
                          } ${isClickable ? "tw-cursor-pointer" : ""}`}
                        >
                          {isClickable ? (
                            <a
                              href={
                                isMemberProfilePageEnabled
                                  ? `/user/${row.original.userUuid}`
                                  : `/members/view/${row.original.userUuid}`
                              }
                              className="tw-no-underline hover:tw-no-underline"
                              rel="noopener noreferrer"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </a>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="tw-h-24 tw-text-center"
                  >
                    <EmptyTable peopleType={activeTab} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
      {showPagination && <TablePagination table={table} />}
    </>
  );
}
